// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { mdiEye, mdiEyeOff } from "@mdi/js";
import { Button } from "antd";
import { facilitiesMapAtom, wellsMapAtom } from "atoms";
import { IS_MCDAN_ENV } from "constants/app.constants";
import {
  FACILITY_LABEL_LAYER,
  FACILITY_LAYER,
  WELL_LABEL_LAYER,
  WELL_LAYER
} from "constants/mapLayers.constants";
import { useSetAtom } from "jotai";
import styled from "styled-components/macro";

import { EntityKind } from "models/entityKind";

import ClientSync from "components/client-sync/ClientSync";
import { useCheckedLayersChangeHandler } from "components/project/layers/hooks";
import { useProjectContext } from "components/project/projects/context";
import Sync from "components/sync/Sync";

import { DataSourceSelect, NormalizeSelect, TimeStepSelect } from ".";
import useBetaFeatures from "../../../hooks/useBetaFeatures";
import { setActiveEntities, setIsTogglingMidstream } from "../../../store/features";
import { RootState } from "../../../store/rootReducer";
import GroupModePopover from "./GroupModePopover";

interface ProductionButtonGroupProps {
  entityKind: EntityKind;
  groupModePopoverOpen?: boolean;
  onGroupModePopoverOpenChange?: (open: boolean) => void;
}

const ProductionButtonGroup = ({
  entityKind,
  groupModePopoverOpen,
  onGroupModePopoverOpenChange
}: ProductionButtonGroupProps) => {
  const activeEntities = useSelector((state: RootState) => state.app.activeEntityKinds);

  const dispatch = useDispatch();
  const { hasFeature } = useBetaFeatures();
  const active = activeEntities.includes(entityKind);
  const setWellsMap = useSetAtom(wellsMapAtom);
  const setFacilitiesMap = useSetAtom(facilitiesMapAtom);
  const projectContext = useProjectContext();
  const { handleCheckedLayersChange } = useCheckedLayersChangeHandler();
  const handleClick = useCallback(() => {
    const willBeActive = !active; // Toggle the current state
    if (willBeActive) {
      // Activate the entity
      if (!activeEntities.includes(entityKind)) {
        dispatch(setActiveEntities([...activeEntities, entityKind]));
      }
      let checkedLayerKeys = [...projectContext.checkedLayerKeys]; // Create copy of existing keys

      // Add layers based on entity type
      if (entityKind === EntityKind.Facility) {
        if (!checkedLayerKeys.includes(FACILITY_LAYER)) {
          checkedLayerKeys = [...checkedLayerKeys, FACILITY_LAYER];
        }
        if (!checkedLayerKeys.includes(FACILITY_LABEL_LAYER)) {
          checkedLayerKeys = [...checkedLayerKeys, FACILITY_LABEL_LAYER];
        }

        handleCheckedLayersChange({
          checkedLayerKeys: checkedLayerKeys,
          checkedTreeKeys: checkedLayerKeys
        });

        dispatch(setIsTogglingMidstream(false));
      } else if (entityKind === EntityKind.Well) {
        if (!checkedLayerKeys.includes(WELL_LAYER)) {
          checkedLayerKeys = [...checkedLayerKeys, WELL_LAYER];
        }
        if (!checkedLayerKeys.includes(WELL_LABEL_LAYER)) {
          checkedLayerKeys = [...checkedLayerKeys, WELL_LABEL_LAYER];
        }

        handleCheckedLayersChange({
          checkedLayerKeys: checkedLayerKeys,
          checkedTreeKeys: checkedLayerKeys
        });
      }
    } else {
      // Deactivate the entity
      dispatch(
        setActiveEntities(
          activeEntities.filter((activeEntity) => activeEntity !== entityKind)
        )
      );

      // Remove layers based on entity type
      if (entityKind === EntityKind.Facility) {
        const updatedLayerKeys = projectContext.checkedLayerKeys.filter(
          (key) => key !== FACILITY_LAYER && key !== FACILITY_LABEL_LAYER
        );

        handleCheckedLayersChange({
          checkedLayerKeys: updatedLayerKeys,
          checkedTreeKeys: updatedLayerKeys
        });

        dispatch(setIsTogglingMidstream(true));

        setFacilitiesMap({
          id: "",
          legend: { legendItems: [], title: "" }
        });
      } else if (entityKind === EntityKind.Well) {
        const updatedLayerKeys = projectContext.checkedLayerKeys.filter(
          (key) => key !== WELL_LAYER && key !== WELL_LABEL_LAYER
        );

        handleCheckedLayersChange({
          checkedLayerKeys: updatedLayerKeys,
          checkedTreeKeys: updatedLayerKeys
        });

        setWellsMap({
          id: "",
          legend: { legendItems: [], title: "" }
        });
      }
    }
  }, [
    entityKind,
    activeEntities,
    projectContext,
    handleCheckedLayersChange,
    dispatch,
    active
  ]);
  return (
    <Wrapper>
      <StyledButton
        data-testid={`production-button-set-active-entity-${entityKind.toLowerCase()}`}
        type={"default"}
        icon={<Icon path={active ? mdiEye : mdiEyeOff} />}
        onClick={handleClick}
      />
      {hasFeature("Group Mode") && (
        <GroupModePopover
          entityKind={entityKind}
          open={groupModePopoverOpen}
          onOpenChange={onGroupModePopoverOpenChange}
        />
      )}
      {!IS_MCDAN_ENV && hasFeature("ClientSync") && entityKind === EntityKind.Well && (
        <ClientSync />
      )}
      {IS_MCDAN_ENV && entityKind === EntityKind.Well && <Sync />}
      {entityKind === EntityKind.Well && (
        <>
          <DataSourceSelect />
          <TimeStepSelect />
          <NormalizeSelect />
        </>
      )}
    </Wrapper>
  );
};

export default ProductionButtonGroup;
const StyledButton = styled(Button)`
  color: #a2aaad;
  &:hover {
    color: var(--color-text);
  }
`;
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  .ant-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2px 6px;
  }
`;
